// Site-wide scroll-speed damping, ADDED 2026-09-01, per Martina ("scroll still
// races through the WHOLE page," after the earlier same-day fixes had already
// widened the two GSAP-scrubbed sections' own scroll budgets and confirmed
// working). Those two fixes were correct but necessarily narrow: they only
// ever bought more scroll DISTANCE for the device-render/statement-reveal
// sections specifically — nothing on this page put any ceiling on how FAST a
// trackpad can move through it, and at ~21,000px of total page height (more
// than half of that inside just those two sections), a couple of confident
// swipes can carry a fast trackpad through several ordinary sections before
// they've been seen at all. No amount of widening one section's CSS
// min-height fixes that, since there's no upper bound on trackpad momentum.
//
// This is Lenis (https://lenis.darkroomengineering.com/) — a small,
// widely-used scroll-normalizer built specifically to solve this class of
// problem, with first-class GSAP ScrollTrigger support (the integration
// below is their own documented pattern, not something invented here). It
// replaces raw wheel/trackpad input with an eased, consistently-paced scroll
// regardless of how aggressive the visitor's own trackpad/OS acceleration
// is — the actual fix for "scrolling itself feels too fast," as opposed to
// "one specific section's animation gets outrun."
//
// Desktop-only (>=990px), matching every other scroll-driven file in this
// theme (cards-scroll.js/how-it-works.js/breath-score.js/device-render.js/
// main.js's own initScrollWordReveal and initHashScroll all gate the exact
// same way) — that's also the exact width where Horizon's own base.css
// hands real scrolling from window/body to .page-wrapper (see this
// project's own history on that), so Lenis is pointed at .page-wrapper to
// match, not window. Below 990px, native touch scrolling is left
// completely untouched — mobile/tablet were never part of this complaint,
// and smoothing touch-driven scroll brings its own class of feel problems
// this pass isn't trying to solve.
import Lenis from "lenis";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

function initScrollSmooth() {
  const pageWrapper = document.querySelector(".page-wrapper");
  if (!pageWrapper) return;

  const desktopQuery = window.matchMedia("(min-width: 990px)");

  // Lenis already checks prefers-reduced-motion itself (respectReducedMotion,
  // on by default) and no-ops its own smoothing when set — matches every
  // other animated section on this page, which all skip themselves under
  // reduced motion too, so this doesn't need its own separate check.

  let lenis = null;
  let rafCallback = null;
  let refreshCallback = null;
  let resizeCheckTimer = null;

  function build() {
    if (!desktopQuery.matches) return;

    lenis = new Lenis({
      wrapper: pageWrapper,
      // Default lerp-based smoothing (no explicit duration/easing) — Lenis's
      // own well-tested default (0.1) rather than a guessed number. This is
      // the one knob to adjust if it still reads as too fast (lower = more
      // damping/slower feel) or starts to feel sluggish (higher = snappier,
      // closer to native) once tested against a real trackpad.
      lerp: 0.1,
    });

    // Standard Lenis+GSAP wiring (Lenis's own documented integration, not
    // custom here): every Lenis scroll tick tells ScrollTrigger to
    // recompute, so the three existing ScrollTrigger-based sections
    // (device-render.js, how-it-works.js, breath-score.js) stay in sync
    // with the now-eased scroll position instead of the raw input.
    lenis.on("scroll", ScrollTrigger.update);

    // FIXED 2026-09-01, found by Martina ("why is there no footer on
    // homepage?" — there was; it had become unreachable). Root cause: with
    // a custom `wrapper` (not window), Lenis measures scroll bounds from
    // `wrapper.scrollHeight` directly — but only re-measures that when its
    // own resize-detection fires, which for a non-window wrapper watches
    // element *box size* changes, not scrollable-content growth. This
    // page's real height keeps growing well after Lenis first constructs
    // (device-render.js's 3D model finishes loading and settles its
    // ScrollTrigger's pin-spacer height; how-it-works.js's own
    // `document.fonts.ready` handler triggers a global `ScrollTrigger
    // .refresh()` once the custom webfont swaps in and reflows the page) —
    // none of that ever changes `.page-wrapper`'s own box size (it's a
    // fixed `100dvh` per Horizon's base.css), so Lenis kept scrolling
    // against its original, stale measurement (confirmed live: `lenis
    // .dimensions.scrollHeight` sat ~1350px short of the page's real,
    // final `scrollHeight`) — clamping the last stretch of the page,
    // footer included, permanently out of reach. GSAP ScrollTrigger
    // already fires a global "refresh" event every time *anything* on the
    // page calls `.refresh()`, for exactly this class of problem — hooking
    // that one event covers every current and future cause of the page
    // growing after load, rather than duplicating each individual
    // font/model-load trigger here too.
    refreshCallback = () => lenis?.resize();
    ScrollTrigger.addEventListener("refresh", refreshCallback);

    rafCallback = (time) => {
      lenis.raf(time * 1000);
    };
    gsap.ticker.add(rafCallback);
    // Lenis already handles its own eased timing internally — GSAP's
    // frame-skipping "lag smoothing" (meant for recovering from a stalled
    // tab) would otherwise fight that by occasionally jumping the ticker
    // forward, same reasoning GSAP's own Lenis integration guide gives for
    // disabling it here.
    gsap.ticker.lagSmoothing(0);

    // Belt-and-braces alongside the ScrollTrigger "refresh" hook above:
    // one extra resize() once everything (images included) has finished
    // loading, in case something ever grows the page late without going
    // through a ScrollTrigger.refresh() call at all.
    window.addEventListener("load", () => lenis?.resize(), { once: true });

    // FIXED 2026-09-01, found by Martina ("scroll all the way down to
    // form... nothing happening... doesn't scroll further to footer" —
    // same underlying class of bug as the earlier footer fix above, one
    // level deeper). The ScrollTrigger "refresh" hook covers the two
    // triggers found so far (webfont swap, 3D model load) — but nothing
    // fires a ScrollTrigger refresh when a `loading="lazy"` image finishes
    // downloading late (this page has several — testimonial/about-team
    // photos, the final-cta product shot), which can still grow the page
    // after Lenis's last measurement with nothing telling it to re-measure.
    //
    // Lenis's own built-in resize detection (a ResizeObserver on its
    // `content` element, document.documentElement by default) can't catch
    // this at all here, structurally — not a timing gap. `.page-wrapper`
    // has a *fixed* height (100dvh, Horizon's own base.css) with
    // `overflow-y: auto`; growing its internal scrollable content never
    // changes `.page-wrapper`'s own box size, and `<html>`'s size is in
    // turn derived from `.page-wrapper`'s (fixed) box, not its clipped
    // overflow content — so neither element's box size ever changes no
    // matter how tall the real scrollable content gets. Confirmed by
    // injecting a simulated 500px of late content and watching
    // `lenis.limit` never update even after a 500ms wait, well past
    // Lenis's own 250ms resize debounce — this is a structural blind spot
    // for ResizeObserver, not a race to win with a longer delay.
    //
    // Fixed with a lightweight poll: check `.page-wrapper`'s actual
    // scrollHeight periodically and call resize() the moment it changes.
    // Brute-force, but it's the only thing that actually sees this class
    // of change (there's no browser API that observes an overflow
    // container's *scroll* size directly, only its box size) — and unlike
    // the "refresh" hook, it needs no knowledge of *why* the page grew, so
    // it also covers any future cause, not just the specific lazy-image
    // one that surfaced this.
    let lastKnownScrollHeight = pageWrapper.scrollHeight;
    resizeCheckTimer = setInterval(() => {
      const currentScrollHeight = pageWrapper.scrollHeight;
      if (currentScrollHeight !== lastKnownScrollHeight) {
        lastKnownScrollHeight = currentScrollHeight;
        lenis?.resize();
      }
    }, 500);
  }

  function teardown() {
    if (rafCallback) {
      gsap.ticker.remove(rafCallback);
      rafCallback = null;
    }
    if (refreshCallback) {
      ScrollTrigger.removeEventListener("refresh", refreshCallback);
      refreshCallback = null;
    }
    if (resizeCheckTimer) {
      clearInterval(resizeCheckTimer);
      resizeCheckTimer = null;
    }
    lenis?.destroy();
    lenis = null;
  }

  // Rebuilds across the 990px line on resize (rotating a tablet, resizing a
  // browser window) — same "sync on resize" pattern this theme already uses
  // in testimonials-carousel.js/cards-scroll.js for their own breakpoints,
  // so crossing the line doesn't get stuck with Lenis either running where
  // it shouldn't or missing where it should.
  function sync() {
    if (desktopQuery.matches && !lenis) build();
    else if (!desktopQuery.matches && lenis) teardown();
  }

  build();

  let resizeTimer;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(sync, 150);
  });

  // Console-inspectable state, same idea as window.__debugDevice /
  // window.__debugHowItWorks elsewhere in this theme.
  window.__debugScrollSmooth = {
    get lenis() {
      return lenis;
    },
  };
}

initScrollSmooth();
